import {
  Add as AddIcon,
  Article as ArticleIcon,
  CheckCircle as ApproveIcon,
  Edit as EditIcon,
  LocationCity as CityIcon,
  Place as LocationIcon,
  Publish as PublishIcon,
  RateReview as ReviewIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/GoogleAuthContext';
import {
  api,
  ContentListItem,
  ContentStats,
  ContentStatus,
  ContentType,
} from '../services/api-client';

const STATUS_COLORS: Record<ContentStatus, 'default' | 'warning' | 'info' | 'success' | 'error'> = {
  draft: 'default',
  in_review: 'warning',
  approved: 'info',
  scheduled: 'info',
  published: 'success',
};

const STATUS_LABELS: Record<ContentStatus, string> = {
  draft: 'Draft',
  in_review: 'In Review',
  approved: 'Approved',
  scheduled: 'Scheduled',
  published: 'Published',
};

const TYPE_ICONS: Record<ContentType, React.ReactElement> = {
  editorial: <ArticleIcon fontSize="small" />,
  city: <CityIcon fontSize="small" />,
  location: <LocationIcon fontSize="small" />,
};

export default function ContentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState<ContentListItem[]>([]);
  const [stats, setStats] = useState<ContentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [generateDialog, setGenerateDialog] = useState(false);
  const [generateTopic, setGenerateTopic] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState<string | null>(null);

  const canEdit = user?.role === 'admin' || user?.effective_permissions?.content?.edit;
  const canPublish = user?.role === 'admin' || user?.effective_permissions?.content?.publish;

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [contentItems, contentStats] = await Promise.all([
        api.getContentList({
          status: statusFilter !== 'all' ? statusFilter : undefined,
          contentType: typeFilter || undefined,
        }),
        api.getContentStats(),
      ]);
      setItems(contentItems);
      setStats(contentStats);
    } catch (err: any) {
      setError(err.message || 'Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [statusFilter, typeFilter]);

  const filteredItems = search
    ? items.filter(
        (item) =>
          item.title.toLowerCase().includes(search.toLowerCase()) ||
          item.slug.toLowerCase().includes(search.toLowerCase())
      )
    : items;

  const handleSubmitReview = async (item: ContentListItem) => {
    setActionLoading(item.slug);
    try {
      await api.submitContentReview({
        contentType: item.contentType,
        slug: item.slug,
        citySlug: item.citySlug,
      });
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to submit for review');
    } finally {
      setActionLoading(null);
    }
  };

  const handleApprove = async (item: ContentListItem) => {
    setActionLoading(item.slug);
    try {
      await api.approveContent({
        contentType: item.contentType,
        slug: item.slug,
        citySlug: item.citySlug,
        approved: true,
      });
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to approve');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePublish = async (item: ContentListItem) => {
    setActionLoading(item.slug);
    try {
      await api.publishContent({
        contentType: item.contentType,
        slug: item.slug,
        citySlug: item.citySlug,
      });
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to publish');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const handleGenerate = async () => {
    if (!generateTopic.trim()) return;
    setGenerating(true);
    setGenerateResult(null);
    setError(null);
    try {
      const result = await api.generateContent({ topic: generateTopic.trim() });
      if (result.editorial_slug) {
        setGenerateResult(`Article created as draft: "${result.editorial_slug}". Refresh to see it.`);
      } else if (result.verification_passed) {
        setGenerateResult('Article generated and verified, but draft submission was skipped (missing metadata). The article content was returned successfully — try again or submit manually.');
      } else {
        setGenerateResult('Article generation completed but quality verification failed. Try a more specific topic.');
      }
      setGenerateTopic('');
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ArticleIcon color="primary" />
          <Typography variant="h5" fontWeight={600}>
            Content Management
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {canEdit && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setGenerateDialog(true)}
              disabled={generating}
            >
              Generate Article
            </Button>
          )}
          <IconButton onClick={fetchData} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {(['draft', 'in_review', 'approved', 'published'] as ContentStatus[]).map((status) => (
            <Grid item xs={6} md={3} key={status}>
              <Card
                sx={{
                  cursor: 'pointer',
                  border: statusFilter === status ? 2 : 1,
                  borderColor: statusFilter === status ? 'primary.main' : 'divider',
                }}
                onClick={() => setStatusFilter(statusFilter === status ? 'all' : status)}
              >
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="body2" color="text.secondary">
                    {STATUS_LABELS[status]}
                  </Typography>
                  <Typography variant="h4" fontWeight={700}>
                    {stats[status] || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search by title or slug..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
          sx={{ minWidth: 250 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="in_review">In Review</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="published">Published</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={typeFilter}
            label="Type"
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <MenuItem value="">All Types</MenuItem>
            <MenuItem value="editorial">Editorial</MenuItem>
            <MenuItem value="city">City Guide</MenuItem>
            <MenuItem value="location">Location</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {filteredItems.length} items
        </Typography>
      </Paper>

      {/* Content Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : filteredItems.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ArticleIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No content found</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell>Source</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.map((item) => (
                <TableRow
                  key={`${item.contentType}-${item.slug}`}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/content/${item.contentType}/${item.slug}`)}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 350 }}>
                      {item.title || item.slug}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.slug}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={TYPE_ICONS[item.contentType]}
                      label={item.contentType}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={STATUS_LABELS[item.status] || item.status}
                      color={STATUS_COLORS[item.status] || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{formatDate(item.updatedAt)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {item.generatedBy === 'ai' ? 'AI' : item.generatedBy || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    {actionLoading === item.slug ? (
                      <CircularProgress size={20} />
                    ) : (
                      <>
                        {item.status === 'draft' && canEdit && (
                          <Tooltip title="Submit for Review">
                            <IconButton size="small" onClick={() => handleSubmitReview(item)}>
                              <ReviewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {item.status === 'in_review' && canPublish && (
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleApprove(item)}
                            >
                              <ApproveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {item.status === 'approved' && canPublish && (
                          <Tooltip title="Publish">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handlePublish(item)}
                            >
                              <PublishIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/content/${item.contentType}/${item.slug}`)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Generate Article Dialog */}
      <Dialog
        open={generateDialog}
        onClose={() => !generating && setGenerateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Generate Article</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter a topic and the AI pipeline will research, write, and submit a draft article.
            This typically takes 3-6 minutes.
          </Typography>
          <TextField
            autoFocus
            label="Topic"
            placeholder='e.g. "Best speakeasy bars in Chicago" or "Off the beaten path in Tokyo"'
            fullWidth
            value={generateTopic}
            onChange={(e) => setGenerateTopic(e.target.value)}
            disabled={generating}
            onKeyDown={(e) => e.key === 'Enter' && !generating && handleGenerate()}
          />
          {generating && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Generating article... This takes 3-6 minutes. Please wait.
              </Typography>
            </Box>
          )}
          {generateResult && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {generateResult}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateDialog(false)} disabled={generating}>
            {generateResult ? 'Close' : 'Cancel'}
          </Button>
          {!generateResult && (
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={generating || !generateTopic.trim()}
            >
              {generating ? 'Generating...' : 'Generate'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
